import React, { useState } from 'react';
import { styled, useStyletron } from 'baseui';
import { FormControl } from 'baseui/form-control';
import { Input, SIZE } from 'baseui/input';
import { useDOMWidth } from '../../../../../../common/hooks/use-dom-width';
import { getWidthOverrides, INPUT_PADDING_HORIZONTAL, INPUT_PADDING_VERTICAL } from '../../../../../labelling-v2/components/frames-control-panel/overrides';
import { TimeDisplayMode } from '../../../../../types';
import { getFrameToTime, getTimeToFrame } from '../../../../../utils/frame-time';
import { MAX_VALUE_CAPTION, MIN_VALUE_CAPTION, SEQUENCE_LABEL_END, SEQUENCE_LABEL_START } from './constants';
type PropsT = {
  setNewRange: React.Dispatch<React.SetStateAction<{
    start: number;
    end: number;
  }>>;
  maxFrames: number;
  currentFrame: number;
  startFrame?: number;
  initialEndFrame?: number;
  timeDisplayMode: TimeDisplayMode;
  frameRate: number | null | undefined;
  totalFrames: number;
};
const StyledLabel = styled('div', ({
  $theme
}) => ({
  ...$theme.typography.font100
}));
const HiddenMeasure = styled('span', ({
  $theme
}) => ({
  ...$theme.typography.LabelMedium,
  position: 'absolute',
  visibility: 'hidden',
  whiteSpace: 'pre',
  padding: 0,
  margin: 0,
  height: 0,
  overflow: 'hidden'
}));
const FORM_CONTROL_WIDTH = '80px';
const FORM_CONTROL_OVERRIDES = {
  ControlContainer: {
    style: {
      marginBottom: 0,
      width: FORM_CONTROL_WIDTH
    }
  },
  Caption: {
    style: {
      whiteSpace: 'nowrap'
    }
  }
};
const getInputOverrides = (calculatedWidth: number) => {
  const widthOverrides = getWidthOverrides(calculatedWidth);
  return {
    Root: {
      props: widthOverrides.props,
      style: ({
        $theme
      }) => ({
        padding: `${INPUT_PADDING_VERTICAL($theme)} ${INPUT_PADDING_HORIZONTAL($theme)}`,
        ...widthOverrides.style({
          $theme
        })
      })
    },
    Input: {
      style: {
        padding: 0
      }
    }
  };
};
export const AnnotationSequence = ({
  setNewRange,
  maxFrames,
  currentFrame,
  startFrame,
  initialEndFrame,
  timeDisplayMode,
  frameRate,
  totalFrames
}: PropsT) => {
  // Start is read-only; display the pinned session start when provided, else the live playhead.
  const startFrameValue = startFrame ?? currentFrame;
  const startDisplayFrame = startFrameValue + 1;
  const startFrameTime = getFrameToTime({
    frame: startFrameValue,
    maxFrames: totalFrames,
    frameRate
  });
  const startInputValue = timeDisplayMode === TimeDisplayMode.FRAMES ? startDisplayFrame : startFrameTime;

  // The caption keeps showing the max allowed end (last frame).
  const defaultEndFrame = maxFrames;
  const defaultEndFrameTime = getFrameToTime({
    frame: maxFrames,
    maxFrames: totalFrames,
    frameRate
  });
  const defaultEndInputValue = timeDisplayMode === TimeDisplayMode.FRAMES ? defaultEndFrame : defaultEndFrameTime;

  // The input's initial value uses the persisted End when provided (keyframe-stepper), else the last
  // frame
  const initialEndDisplayFrame = (initialEndFrame ?? maxFrames - 1) + 1;
  const initialEndFrameTime = getFrameToTime({
    frame: initialEndDisplayFrame,
    maxFrames: totalFrames,
    frameRate
  });
  const initialEndInputValue = timeDisplayMode === TimeDisplayMode.FRAMES ? initialEndDisplayFrame : initialEndFrameTime;
  const [endInputValue, setEndInputValue] = useState(initialEndInputValue);
  const totalTime = getFrameToTime({
    frame: totalFrames - 1,
    maxFrames: totalFrames,
    frameRate
  });
  const totalTimeMax = String(totalTime).replace(/[0-9]/g, '0');
  const {
    width: totalTimeWidth,
    measureRef: totalTimeMeasureRef
  } = useDOMWidth();
  const [css, theme] = useStyletron();
  const handleEndInputValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedValue = e.target.value;
    setEndInputValue(updatedValue);
    const trimmedUpdatedValue = updatedValue.trim();
    let updatedFrameNumber;
    if (timeDisplayMode === TimeDisplayMode.FRAMES) {
      updatedFrameNumber = Number(trimmedUpdatedValue) - 1;
    } else {
      const timeFrameNumber = getTimeToFrame(trimmedUpdatedValue, frameRate);
      updatedFrameNumber = timeFrameNumber;
    }
    setNewRange(prev => ({
      ...prev,
      end: updatedFrameNumber
    }));
  };
  return <div className={css({
    display: 'flex',
    padding: theme.sizing.scale100,
    width: '100%'
  })}>
      <HiddenMeasure ref={totalTimeMeasureRef} data-testid="time-hidden-measure">
        <StyledLabel>{totalTimeMax}</StyledLabel>
      </HiddenMeasure>
      <div className={css({
      flexGrow: 1
    })}>
        <FormControl label={() => SEQUENCE_LABEL_START} caption={`${MIN_VALUE_CAPTION} ${startInputValue}`} overrides={FORM_CONTROL_OVERRIDES}>
          <Input size={SIZE.mini} value={startInputValue} disabled overrides={getInputOverrides(totalTimeWidth)} />
        </FormControl>
      </div>
      <div className={css({
      flexGrow: 1
    })}>
        <FormControl label={() => SEQUENCE_LABEL_END} caption={`${MAX_VALUE_CAPTION} ${defaultEndInputValue}`} overrides={FORM_CONTROL_OVERRIDES}>
          <Input size={SIZE.mini} onChange={handleEndInputValueChange} value={endInputValue} overrides={getInputOverrides(totalTimeWidth)} />
        </FormControl>
      </div>
    </div>;
};